import { useEffect, useState } from "react";
import { Trash2, Download, Mail, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { adminGetEnquiries, adminUpdateEnquiryStatus, adminDeleteEnquiry, enquiriesCsvUrl } from "./adminApi";
import { Header } from "./AdminTours";

const STATUSES = ["new", "contacted", "closed"];

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => adminGetEnquiries().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (e, status) => { await adminUpdateEnquiryStatus(e.id, status); toast.success("Status updated"); load(); if (selected?.id === e.id) setSelected({ ...selected, status }); };
  const del = async (e) => { if (!window.confirm("Delete this enquiry?")) return; await adminDeleteEnquiry(e.id); toast.success("Deleted"); setSelected(null); load(); };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("wan_admin_token");
      const res = await fetch(enquiriesCsvUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "enquiries.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const filtered = filter === "all" ? items : items.filter((e) => e.status === filter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div><div className="text-[11px] tracking-[0.3em] uppercase text-[#7c1d1d] font-semibold">Manage</div><h1 className="font-serif text-4xl text-[#3b1a1a] mt-1">Enquiries <span className="text-[#d4af37] text-2xl">({items.length})</span></h1></div>
        <button onClick={downloadCsv} data-testid="export-csv" className="inline-flex items-center gap-2 bg-[#14523d] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <div className="flex gap-2 mb-5">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 text-xs tracking-[0.12em] uppercase border ${filter === s ? "bg-[#7c1d1d] text-white border-[#7c1d1d]" : "border-[#d4af37]/40 text-[#3b1a1a]"}`}>{s} {s !== "all" && `(${items.filter((e) => e.status === s).length})`}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#d4af37]/25" data-testid="enquiries-table">
          {filtered.map((e) => (
            <button key={e.id} onClick={() => setSelected(e)} className={`w-full text-left flex items-center gap-4 px-5 py-4 border-b border-[#d4af37]/15 last:border-0 hover:bg-[#faf8f5] ${selected?.id === e.id ? "bg-[#faf8f5]" : ""}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#3b1a1a]">{e.name} <span className="text-[#5a4b4b] font-normal text-sm">· {e.country}</span></div>
                <div className="text-sm text-[#5a4b4b] clamp-2">{e.preferred_tour || e.message || e.email}</div>
                <div className="text-[11px] text-[#5a4b4b]/70 mt-1">{new Date(e.created_at).toLocaleString("en-GB")}</div>
              </div>
              <span className={`text-[10px] uppercase px-2 py-1 shrink-0 ${e.status === "new" ? "bg-[#7c1d1d] text-white" : e.status === "contacted" ? "bg-[#d4af37] text-[#2d1616]" : "bg-[#14523d] text-white"}`}>{e.status}</span>
            </button>
          ))}
          {filtered.length === 0 && <div className="px-5 py-10 text-center text-[#5a4b4b]">No enquiries.</div>}
        </div>

        {/* Detail */}
        <div className="lg:col-span-1">
          {selected ? (
            <div className="bg-white border border-[#d4af37]/25 p-6 lg:sticky lg:top-6" data-testid="enquiry-detail">
              <div className="font-serif text-2xl text-[#3b1a1a]">{selected.name}</div>
              <div className="text-sm text-[#5a4b4b]">{selected.country}</div>
              <div className="mt-4 space-y-2 text-sm">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-[#3b1a1a] hover:text-[#7c1d1d]"><Mail className="w-4 h-4 text-[#7c1d1d]" /> {selected.email}</a>
                {selected.phone && <div className="flex items-center gap-2 text-[#3b1a1a]"><Phone className="w-4 h-4 text-[#7c1d1d]" /> {selected.phone}</div>}
                {selected.whatsapp && <a href={`https://wa.me/${selected.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#3b1a1a] hover:text-[#14523d]"><MessageCircle className="w-4 h-4 text-[#14523d]" /> {selected.whatsapp}</a>}
              </div>
              <dl className="mt-4 text-sm space-y-1.5 border-t border-[#d4af37]/20 pt-4">
                <Row k="Travel Dates" v={selected.travel_dates} />
                <Row k="Travellers" v={`${selected.adults} adults, ${selected.children} children`} />
                <Row k="Preferred Tour" v={selected.preferred_tour} />
              </dl>
              {selected.message && <div className="mt-4 bg-[#faf8f5] border border-[#d4af37]/20 p-3 text-sm text-[#4a4a4a]">{selected.message}</div>}
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5a4b4b] mb-2">Update Status</div>
                <div className="flex gap-2">{STATUSES.map((s) => <button key={s} onClick={() => setStatus(selected, s)} className={`flex-1 py-2 text-[11px] uppercase tracking-[0.1em] border ${selected.status === s ? "bg-[#7c1d1d] text-white border-[#7c1d1d]" : "border-[#d4af37]/40 text-[#3b1a1a]"}`}>{s}</button>)}</div>
              </div>
              <button onClick={() => del(selected)} className="mt-5 w-full flex items-center justify-center gap-2 text-[#7c1d1d] border border-[#7c1d1d]/30 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-[#7c1d1d] hover:text-white transition-colors"><Trash2 className="w-4 h-4" /> Delete</button>
            </div>
          ) : (
            <div className="bg-white border border-[#d4af37]/25 p-8 text-center text-[#5a4b4b]">Select an enquiry to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-3"><dt className="text-[#5a4b4b]">{k}</dt><dd className="text-[#3b1a1a] text-right">{v || "—"}</dd></div>;
}
